// @ts-nocheck
import React, { useRef, useState } from "react";
import { format, parseISO } from "date-fns";

import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckIcon from "@material-ui/icons/Check";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import api from "../../services/api";
import { useToast } from "../../hooks/toast";

import {
  Card,
  CardTopo,
  CardCorpo,
  Selo,
  Infos,
  Bloco,
  Dado,
  BotaoIcone,
  Botao,
  BotaoLink,
  LinkTexto,
  ListaAux,
  Andamento,
} from "./styles";

const TAMANHO_MAXIMO_MB = 5;
const ACEITOS = "application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif";

const SELO = { cliente: "SUA VEZ", equipe: "COM A GENTE", concluida: "CONCLUÍDA" };
const FORMA = { pix: "Pix", transferencia: "Transferência", boleto: "Boleto" };

const real = (valor) =>
  typeof valor === "number" ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "A confirmar";

const data = (iso) => {
  try {
    return iso ? format(parseISO(iso), "dd/MM") : "";
  } catch (e) {
    return "";
  }
};

// Título e frase de cada etapa, conforme de quem é a vez.
function textos(e) {
  switch (e.etapa) {
    case "requerimento":
      return e.vez === "cliente"
        ? ["Preencha e assine o requerimento", "Este cartório só emite com um requerimento assinado. Baixe, preencha, assine e envie aqui junto com os documentos pedidos abaixo."]
        : ["Recebemos o requerimento", "Vamos enviar ao cartório junto com os documentos. Nada a fazer agora."];
    case "orcamento":
      return e.custasPeloCliente
        ? ["Pedimos o orçamento ao cartório", "Assim que o cartório responder, mostramos aqui o valor e como pagar."]
        : ["Pedimos a certidão ao cartório", "Estamos acertando o pedido com o cartório. Nada a fazer agora."];
    case "pagamento":
      if (!e.custasPeloCliente) return ["Estamos pagando as custas do cartório", "As custas desta certidão são com a gente. Nada a fazer agora."];
      return e.vez === "cliente"
        ? ["Pague as custas do cartório", "O cartório mandou o orçamento. Pague direto a ele pela forma abaixo e anexe o comprovante. A emissão começa quando o cartório confirmar."]
        : ["Comprovante recebido", "Estamos conferindo o pagamento com o cartório. Nada a fazer agora."];
    case "emitindo":
      return ["O cartório está emitindo a certidão", "O pagamento foi confirmado. Avisamos quando a certidão for despachada."];
    case "a_caminho":
      return ["A certidão está a caminho", "O cartório emitiu e despachou para o nosso escritório. Nada a fazer agora."];
    default:
      return ["Certidão recebida e conferida", "A certidão chegou ao nosso escritório e foi conferida. Ela já entrou no seu processo e aparece como recebida no card do pedido."];
  }
}

function passos(e) {
  const lista = [
    { id: "dados", nome: "Seus dados recebidos", data: data(e.criadoEm) },
    ...(e.etapa === "requerimento" ? [{ id: "requerimento", nome: "Requerimento e documentos" }] : []),
    { id: "orcamento", nome: "Orçamento do cartório" },
    { id: "pagamento", nome: "Pagamento das custas" },
    { id: "emitindo", nome: "Cartório emitindo" },
    { id: "a_caminho", nome: "A caminho do nosso escritório" },
    { id: "recebida", nome: "Recebida e conferida", data: data(e.concluidaEm) },
  ];
  const atual = e.vez === "concluida" ? lista.length : lista.findIndex((p) => p.id === e.etapa);
  return { lista, atual };
}

function copiar(texto, addToast, oQue) {
  const ok = () => addToast({ type: "success", title: `${oQue} copiado` });
  if (navigator.clipboard) {
    navigator.clipboard.writeText(texto).then(ok, () => addToast({ type: "error", title: "Não foi possível copiar" }));
  }
}

export function CardEmissao({ emissao: e, onAtualizada, onComoAssinar }) {
  const { addToast } = useToast();
  const inputRef = useRef(null);
  const [envio, setEnvio] = useState(null); // { tipo, documento } em andamento
  const [titulo, frase] = textos(e);
  const { lista, atual } = passos(e);

  const escolherArquivo = (tipo, documento) => {
    if (envio) return;
    inputRef.current.dataset.tipo = tipo;
    inputRef.current.dataset.documento = documento || "";
    inputRef.current.click();
  };

  const enviar = async (event) => {
    const file = event.target.files && event.target.files[0];
    const { tipo, documento } = inputRef.current.dataset;
    inputRef.current.value = "";
    if (!file) return;

    if (file.size > TAMANHO_MAXIMO_MB * 1024 * 1024) {
      addToast({ type: "error", title: "Arquivo muito grande", description: `Envie uma foto ou um PDF de até ${TAMANHO_MAXIMO_MB} MB.` });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("tipo", tipo);
    if (documento) formData.append("documento", documento);
    setEnvio({ tipo, documento });

    try {
      const response = await api.post(`central/emissoes/${e.id}/arquivo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onAtualizada(e.id, response?.data?.emissao);
      addToast({
        type: "success",
        title: tipo === "comprovante" ? "Comprovante enviado" : "Arquivo enviado",
        description: "A equipe já recebeu e vai conferir.",
      });
    } catch (err) {
      addToast({
        type: "error",
        title: "Não foi possível enviar",
        description: err?.response?.data?.message || "Tente de novo em instantes. Se continuar, fale com o nosso suporte.",
      });
    } finally {
      setEnvio(null);
    }
  };

  const enviando = (tipo, documento) => envio && envio.tipo === tipo && (envio.documento || "") === (documento || "");
  const pg = e.pagamento;
  const req = e.requerimento;

  return (
    <Card>
      <input ref={inputRef} type="file" accept={ACEITOS} hidden onChange={enviar} />

      <CardTopo>
        <p className="eyebrow">Emissão pela CJ</p>
        <h2>{e.certidao}</h2>
        <p className="sub">
          {e.deQuem ? `${e.deQuem} · ` : ""}Para o pedido de {e.requerente}
        </p>
        {e.pt && (
          <button className="pt" type="button" aria-label={`Copiar número do pedido ${e.pt}`} onClick={() => copiar(e.pt, addToast, "Número do pedido")}>
            {e.pt} <FileCopyOutlinedIcon />
          </button>
        )}
      </CardTopo>

      <CardCorpo>
        <Selo vez={e.vez}>
          <i />
          {SELO[e.vez]}
        </Selo>
        <h3>{titulo}</h3>
        <p className="desc">{frase}</p>

        {(pg || e.cartorio) && e.etapa !== "recebida" && (
          <Infos>
            {pg && (
              <div>
                <small>Valor do cartório</small>
                <strong>{real(pg.valor)}</strong>
              </div>
            )}
            {e.cartorio && (
              <div style={pg ? undefined : { gridColumn: "1 / -1" }}>
                <small>Cartório</small>
                <strong>{e.cartorio}</strong>
              </div>
            )}
          </Infos>
        )}

        {pg && (
          <>
            <Bloco>
              <div className="bloco-topo">
                <p className="bloco-titulo">Como pagar</p>
                {pg.forma && <span className="etiqueta">{FORMA[pg.forma]}</span>}
              </div>
              {pg.dados ? (
                <Dado>
                  <pre>{pg.dados}</pre>
                  <BotaoIcone type="button" aria-label="Copiar dados de pagamento" onClick={() => copiar(pg.dados, addToast, "Dados de pagamento")}>
                    <FileCopyOutlinedIcon />
                  </BotaoIcone>
                </Dado>
              ) : (
                !pg.boleto.length && <p className="nota">Os dados de pagamento ainda não chegaram. Assim que o cartório mandar, eles aparecem aqui.</p>
              )}
              {pg.boleto.map((b) => (
                <BotaoLink key={b.url} secundario href={b.url} target="_blank" rel="noopener noreferrer">
                  <GetAppIcon /> Baixar boleto (PDF)
                </BotaoLink>
              ))}
              {pg.forma === "boleto" && <p className="nota">Pague pelo app do banco com o código ou com o PDF. Depois do vencimento, fale com a gente para pedir outro boleto.</p>}
              {pg.forma === "pix" && <p className="nota">Antes de pagar, confira se o favorecido é o cartório.</p>}
            </Bloco>
            <Botao type="button" secundario={pg.comprovanteEnviado} disabled={Boolean(envio)} onClick={() => escolherArquivo("comprovante")}>
              <AttachFileIcon />
              {enviando("comprovante") ? "Enviando…" : pg.comprovanteEnviado ? "Enviar outro comprovante" : "Anexar comprovante"}
            </Botao>
          </>
        )}

        {req && (
          <>
            {req.modelo.map((m, i) => (
              <BotaoLink key={m.url} href={m.url} target="_blank" rel="noopener noreferrer" download={m.nome}>
                <GetAppIcon /> {req.modelo.length > 1 ? `Baixar requerimento (${i + 1})` : "Baixar requerimento"}
              </BotaoLink>
            ))}
            {!req.modelo.length && <p className="desc">O modelo do requerimento ainda está sendo preparado. Ele aparece aqui para baixar.</p>}
            <Botao type="button" secundario disabled={Boolean(envio)} onClick={() => escolherArquivo("requerimento")}>
              {req.enviado ? <CheckIcon /> : <AttachFileIcon />}
              {enviando("requerimento") ? "Enviando…" : req.enviado ? "Requerimento enviado · enviar de novo" : "Enviar requerimento assinado"}
            </Botao>
            <LinkTexto type="button" onClick={() => onComoAssinar(e)}>
              Como preencher e assinar <ChevronRightIcon />
            </LinkTexto>

            {req.auxiliares.length > 0 && (
              <Bloco>
                <div className="bloco-topo">
                  <p className="bloco-titulo">Documentos auxiliares</p>
                  <span className={`etiqueta ${req.auxiliares.every((a) => a.enviado) ? "" : "pendente"}`}>
                    {req.auxiliares.filter((a) => a.enviado).length} de {req.auxiliares.length} enviados
                  </span>
                </div>
                <p className="nota">O cartório pede estes documentos junto com o requerimento.</p>
                <ListaAux>
                  {req.auxiliares.map((a) => (
                    <li key={a.documento} className={a.enviado ? "enviado" : ""}>
                      <div>
                        <strong>{a.documento === "RG de quem assinou" ? "RG ou CNH de quem assinou o requerimento" : a.documento}</strong>
                        <small>{a.enviado ? a.arquivo : a.documento === "Certidão de óbito" ? "Caso a pessoa da certidão tenha falecido." : "Frente e verso, legível (pode ser o digital)."}</small>
                      </div>
                      {a.enviado ? (
                        <span className="ok">
                          <CheckIcon /> Enviado
                        </span>
                      ) : (
                        <button type="button" disabled={Boolean(envio)} onClick={() => escolherArquivo("auxiliar", a.documento)}>
                          <AttachFileIcon /> {enviando("auxiliar", a.documento) ? "Enviando…" : "Enviar"}
                        </button>
                      )}
                    </li>
                  ))}
                </ListaAux>
              </Bloco>
            )}
          </>
        )}

        {e.rastreio && e.etapa === "a_caminho" && (
          <Dado>
            <div style={{ minWidth: 0 }}>
              <small style={{ display: "block", fontSize: 11, color: "var(--cinza-70)" }}>Código de rastreio</small>
              <pre>{e.rastreio}</pre>
            </div>
            <BotaoIcone type="button" aria-label="Copiar código de rastreio" onClick={() => copiar(e.rastreio, addToast, "Código de rastreio")}>
              <FileCopyOutlinedIcon />
            </BotaoIcone>
          </Dado>
        )}

        {e.vez === "concluida" && (
          <LinkTexto as="a" href="/">
            Ver no pedido {e.pt} <ChevronRightIcon />
          </LinkTexto>
        )}

        <Andamento>
          <div className="topo">
            <strong>Andamento</strong>
            <span>
              Passo {Math.min(atual + 1, lista.length)} de {lista.length}
            </span>
          </div>
          <ol>
            {lista.map((p, i) => {
              const classe = i < atual ? "feito" : i === atual ? e.vez : "";
              return (
                <li key={p.id} className={classe}>
                  <span className="ponto">{i < atual && <CheckIcon />}</span>
                  <span className="texto">
                    {i < atual ? <b>{p.nome}</b> : p.nome}
                    {i === atual && e.vez === "cliente" ? " · sua vez" : ""}
                    {i < atual && p.data ? p.data : ""}
                  </span>
                </li>
              );
            })}
          </ol>
        </Andamento>
      </CardCorpo>
    </Card>
  );
}
